/**
 * Raid commander gesture state machine — Phaser-free.
 * Scene relays pointer events; this module never mutates sim.
 */

export type GestureKnobs = {
  dashMaxDist: number;
  minSwipePx: number;
  dragStartPx: number;
  pinDelaySec: number;
  areaRMin: number;
  areaRMax: number;
  areaGrowPxPerSec: number;
  unitHitPad: number;
};

export type GestureHit = {
  unitId: number;
  x: number;
  y: number;
  radius: number;
};

export type GestureMode =
  | "idle"
  | "unitPress"
  | "unitSwipe"
  | "pinning"
  | "areaHold"
  | "areaSwipe";

/** HUD-facing prep phase (toggle-like) — maps 1:1 from GestureMode. */
export type GesturePrepKind =
  | "idle"
  | "holdUnit"
  | "holdEmpty"
  | "aimDash"
  | "pinCharged"
  | "aimGroupDash";

export type GesturePrep = {
  kind: GesturePrepKind;
  /** Unit under prep when relevant; -1 otherwise. */
  unitId: number;
};

export type GestureEmit =
  | { type: "dash"; unitId: number; tx: number; ty: number }
  | {
      type: "groupDash";
      cx: number;
      cy: number;
      radius: number;
      tx: number;
      ty: number;
    }
  | { type: "pinStart"; unitId: number }
  | { type: "pinEnd"; unitId: number };

export type GestureVisual =
  | { kind: "none" }
  | {
      kind: "unitAim";
      unitId: number;
      x0: number;
      y0: number;
      x1: number;
      y1: number;
      valid: boolean;
    }
  | { kind: "areaGrow"; cx: number; cy: number; r: number; pulse: number }
  | {
      kind: "areaAim";
      cx: number;
      cy: number;
      r: number;
      x0: number;
      y0: number;
      x1: number;
      y1: number;
      valid: boolean;
    }
  | { kind: "pin"; unitId: number; x: number; y: number };

/** Ally ids that should show ground select rings for the current aim visual. */
export function gestureSelectTargets(
  v: GestureVisual,
  units: readonly {
    id: number;
    x: number;
    y: number;
    team: number;
    hp: number;
  }[],
): number[] {
  if (v.kind === "unitAim") {
    for (const u of units) {
      if (u.id === v.unitId && u.team === 0 && u.hp > 0) return [u.id];
    }
    return [];
  }
  if (v.kind === "areaGrow" || v.kind === "areaAim") {
    const r2 = v.r * v.r;
    const out: number[] = [];
    for (const u of units) {
      if (u.team !== 0 || u.hp <= 0) continue;
      const dx = u.x - v.cx;
      const dy = u.y - v.cy;
      if (dx * dx + dy * dy <= r2) out.push(u.id);
    }
    return out;
  }
  return [];
}

export type GestureState = {
  mode: GestureMode;
  knobs: GestureKnobs;
  downAt: number;
  startX: number;
  startY: number;
  curX: number;
  curY: number;
  unitId: number;
  unitX: number;
  unitY: number;
  unitRadius: number;
  areaR: number;
  pinnedUnitId: number;
};

export function createGestureState(knobs: GestureKnobs): GestureState {
  return {
    mode: "idle",
    knobs,
    downAt: 0,
    startX: 0,
    startY: 0,
    curX: 0,
    curY: 0,
    unitId: -1,
    unitX: 0,
    unitY: 0,
    unitRadius: 0,
    areaR: knobs.areaRMin,
    pinnedUnitId: -1,
  };
}

function swipeLen(gs: GestureState): number {
  return Math.hypot(gs.curX - gs.startX, gs.curY - gs.startY);
}

function dashTarget(gs: GestureState, fromX: number, fromY: number): { tx: number; ty: number } {
  const dx = gs.curX - gs.startX;
  const dy = gs.curY - gs.startY;
  const len = Math.hypot(dx, dy) || 1;
  const dist = Math.min(gs.knobs.dashMaxDist, len);
  return {
    tx: fromX + (dx / len) * dist,
    ty: fromY + (dy / len) * dist,
  };
}

function areaRadiusAt(gs: GestureState, time: number): number {
  const held = Math.max(0, time - gs.downAt);
  return Math.min(
    gs.knobs.areaRMax,
    gs.knobs.areaRMin + gs.knobs.areaGrowPxPerSec * held,
  );
}

/** Hit pad: dist ≤ max(radius×1.8, unitHitPad). */
export function hitTestAllies(
  x: number,
  y: number,
  units: readonly { id: number; x: number; y: number; radius: number; team: number; hp: number }[],
  unitHitPad: number,
): GestureHit | null {
  let best: GestureHit | null = null;
  let bestD = Infinity;
  for (const u of units) {
    if (u.team !== 0 || u.hp <= 0) continue;
    const pad = Math.max(u.radius * 1.8, unitHitPad);
    const d = Math.hypot(x - u.x, y - u.y);
    if (d <= pad && d < bestD) {
      bestD = d;
      best = { unitId: u.id, x: u.x, y: u.y, radius: u.radius };
    }
  }
  return best;
}

export function gestureDown(
  gs: GestureState,
  x: number,
  y: number,
  time: number,
  hit: GestureHit | null,
): void {
  gs.startX = x;
  gs.startY = y;
  gs.curX = x;
  gs.curY = y;
  gs.downAt = time;
  if (hit) {
    gs.mode = "unitPress";
    gs.unitId = hit.unitId;
    gs.unitX = hit.x;
    gs.unitY = hit.y;
    gs.unitRadius = hit.radius;
  } else {
    gs.mode = "areaHold";
    gs.unitId = -1;
    gs.areaR = gs.knobs.areaRMin;
  }
}

export function gestureMove(gs: GestureState, x: number, y: number, _time: number): void {
  if (gs.mode === "idle") return;
  gs.curX = x;
  gs.curY = y;
  const moved = swipeLen(gs);
  if (gs.mode === "unitPress" && moved > gs.knobs.dragStartPx) {
    gs.mode = "unitSwipe";
  } else if (gs.mode === "pinning" && moved > gs.knobs.dragStartPx) {
    // Hold-then-flick: keep pin on sim until up; aim as unit swipe (prep=aimDash).
    gs.mode = "unitSwipe";
    gs.unitId = gs.pinnedUnitId;
  } else if (gs.mode === "areaHold" && moved > gs.knobs.dragStartPx) {
    gs.mode = "areaSwipe";
    // Freeze radius at transition.
  }
}

export function gestureTick(gs: GestureState, time: number): GestureEmit[] {
  const out: GestureEmit[] = [];
  if (gs.mode === "unitPress") {
    const held = time - gs.downAt;
    if (held >= gs.knobs.pinDelaySec && swipeLen(gs) <= gs.knobs.dragStartPx) {
      gs.mode = "pinning";
      gs.pinnedUnitId = gs.unitId;
      out.push({ type: "pinStart", unitId: gs.unitId });
    }
  } else if (gs.mode === "areaHold") {
    gs.areaR = areaRadiusAt(gs, time);
  }
  return out;
}

export function gestureUp(
  gs: GestureState,
  x: number,
  y: number,
  _time: number,
): GestureEmit[] {
  gs.curX = x;
  gs.curY = y;
  const out: GestureEmit[] = [];
  const len = swipeLen(gs);
  const mode = gs.mode;

  if (mode === "unitSwipe") {
    // Pin→flick: clear pin first, then dash if past minSwipe (same thresholds as unit swipe).
    if (gs.pinnedUnitId >= 0) {
      out.push({ type: "pinEnd", unitId: gs.pinnedUnitId });
    }
    if (len >= gs.knobs.minSwipePx) {
      const t = dashTarget(gs, gs.unitX, gs.unitY);
      out.push({ type: "dash", unitId: gs.unitId, tx: t.tx, ty: t.ty });
    }
  } else if (mode === "pinning") {
    out.push({ type: "pinEnd", unitId: gs.pinnedUnitId });
  } else if (mode === "areaSwipe") {
    if (len >= gs.knobs.minSwipePx) {
      const t = dashTarget(gs, gs.startX, gs.startY);
      out.push({
        type: "groupDash",
        cx: gs.startX,
        cy: gs.startY,
        radius: gs.areaR,
        tx: t.tx,
        ty: t.ty,
      });
    }
  }
  // unitPress short tap / areaHold without swipe → noop
  resetIdle(gs);
  return out;
}

export function gestureCancel(gs: GestureState): GestureEmit[] {
  const out: GestureEmit[] = [];
  // Cover pinning and pin→unitSwipe (pinnedUnitId retained until reset).
  if (gs.pinnedUnitId >= 0) {
    out.push({ type: "pinEnd", unitId: gs.pinnedUnitId });
  }
  resetIdle(gs);
  return out;
}

function resetIdle(gs: GestureState): void {
  gs.mode = "idle";
  gs.unitId = -1;
  gs.pinnedUnitId = -1;
  gs.areaR = gs.knobs.areaRMin;
}

export function gestureVisual(gs: GestureState, time: number): GestureVisual {
  if (gs.mode === "unitSwipe") {
    return {
      kind: "unitAim",
      unitId: gs.unitId,
      x0: gs.unitX,
      y0: gs.unitY,
      x1: gs.curX,
      y1: gs.curY,
      valid: swipeLen(gs) >= gs.knobs.minSwipePx,
    };
  }
  if (gs.mode === "pinning") {
    return {
      kind: "pin",
      unitId: gs.pinnedUnitId,
      x: gs.unitX,
      y: gs.unitY,
    };
  }
  if (gs.mode === "areaHold") {
    const r = areaRadiusAt(gs, time);
    const pulse = 0.5 + 0.5 * Math.sin(time * 8);
    return { kind: "areaGrow", cx: gs.startX, cy: gs.startY, r, pulse };
  }
  if (gs.mode === "areaSwipe") {
    return {
      kind: "areaAim",
      cx: gs.startX,
      cy: gs.startY,
      r: gs.areaR,
      x0: gs.startX,
      y0: gs.startY,
      x1: gs.curX,
      y1: gs.curY,
      valid: swipeLen(gs) >= gs.knobs.minSwipePx,
    };
  }
  return { kind: "none" };
}

/** Pure HUD report for commander prep chip — no sim mutation. */
export function gesturePrep(gs: GestureState): GesturePrep {
  switch (gs.mode) {
    case "unitPress":
      return { kind: "holdUnit", unitId: gs.unitId };
    case "unitSwipe":
      return { kind: "aimDash", unitId: gs.unitId };
    case "pinning":
      return { kind: "pinCharged", unitId: gs.pinnedUnitId };
    case "areaHold":
      return { kind: "holdEmpty", unitId: -1 };
    case "areaSwipe":
      return { kind: "aimGroupDash", unitId: -1 };
    default:
      return { kind: "idle", unitId: -1 };
  }
}
